import React, { useState, useEffect, useContext } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  Card,
  Badge,
  Accordion,
  Modal,
  Alert
} from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Message from '../components/Message.js'
import Loader from '../components/Loader.js'
import {
  getOwnerDetails,
  updateOwnerProfile,
  logoutOwner,
  disableOwner,
  deleteOwner
} from '../actions/ownerActions.js'
import { OWNER_UPDATE_PROFILE_RESET } from '../constants/ownerConstants.js'
import { ThemeContext } from '../components/ThemeContext.js'

const billingBadgeVariant = (status) => {
  switch (status) {
    case 'active':
      return 'success'
    case 'trialing':
      return 'info'
    case 'past_due':
      return 'warning'
    case 'canceled':
      return 'danger'
    default:
      return 'secondary'
  }
}

const OwnerProfileScreen = () => {
  const { theme } = useContext(ThemeContext)

  // Password change
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwMessage, setPwMessage] = useState(null)

  // Copy to clipboard
  const [cidCopied, setCidCopied] = useState(false)

  // Danger zone modals
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const ownerDetails = useSelector((state) => state.ownerDetails)
  const { loading, error, owner } = ownerDetails

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const id = ownerInfo ? ownerInfo._id : null

  const ownerUpdateProfile = useSelector((state) => state.ownerUpdateProfile)
  const { success } = ownerUpdateProfile

  const ownerDisableState = useSelector((state) => state.ownerDisable) || {}
  const ownerDeleteState = useSelector((state) => state.ownerDelete) || {}

  useEffect(() => {
    if (!ownerInfo) {
      navigate('/ownerlogin')
    } else {
      if (!owner || !owner.name || success) {
        dispatch({ type: OWNER_UPDATE_PROFILE_RESET })
        dispatch(getOwnerDetails(ownerInfo._id))
      }
    }
  }, [dispatch, navigate, ownerInfo, owner, success])

  useEffect(() => {
    if (ownerDeleteState.success) {
      dispatch(logoutOwner(id))
    }
  }, [ownerDeleteState.success, dispatch, id])

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => {
      setter(true)
      setTimeout(() => setter(false), 2000)
    })
  }

  const passwordSubmitHandler = (e) => {
    e.preventDefault()
    setPwMessage(null)

    if (password === '') {
      setPwMessage({ type: 'warning', text: 'Password cannot be empty' })
      return
    }
    if (password !== confirmPassword) {
      setPwMessage({ type: 'danger', text: 'Passwords do not match' })
      return
    }

    dispatch(updateOwnerProfile({ id, password }))
    setPwMessage({ type: 'success', text: 'Password updated successfully' })
    setPassword('')
    setConfirmPassword('')
  }

  const handleDisableConfirm = () => {
    dispatch(disableOwner(id))
    setShowDisableModal(false)
    dispatch(logoutOwner(id))
  }

  const handleDeleteConfirm = () => {
    if (confirmText === owner.bname) {
      dispatch(deleteOwner(id))
      setShowDeleteModal(false)
    }
  }

  // Initials for avatar
  const initials =
    owner && owner.fname && owner.lname
      ? `${owner.fname[0]}${owner.lname[0]}`.toUpperCase()
      : owner && owner.name
        ? owner.name.slice(0, 2).toUpperCase()
        : '?'

  const lastLoginDate =
    owner && owner.lastLogin
      ? new Date(owner.lastLogin).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : 'N/A'

  const lastLoginLocation =
    owner && owner.lastloginip
      ? [
          owner.lastloginip.ipcity,
          owner.lastloginip.ipregion,
          owner.lastloginip.ipcountry
        ]
          .filter(Boolean)
          .join(', ') || 'Unknown'
      : 'Unknown'

  const billingPeriodDisplay =
    owner && owner.billingPeriodStart
      ? new Date(owner.billingPeriodStart).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : 'N/A'

  const cardStyle = {
    backgroundColor: theme.card,
    color: theme.text,
    borderColor: theme.sidebarAccent,
    borderRadius: '8px'
  }

  const footerStyle = {
    backgroundColor: theme.surfaceMuted,
    color: theme.subtext,
    borderColor: theme.sidebarAccent
  }

  const accentBtn = {
    borderColor: theme.accent,
    color: theme.accent,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.accent}`
  }

  if (loading) return <Loader />
  if (error) return <Message variant='danger'>{error}</Message>

  return (
    <>
      {/* ── Section 1: Profile Summary Card ─────────────────── */}
      <Card className='mb-4' style={cardStyle}>
        <Card.Body>
          <Row className='align-items-center'>
            <Col xs='auto'>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: theme.accent,
                  color: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.4rem',
                  fontWeight: 700
                }}
              >
                {initials}
              </div>
            </Col>
            <Col>
              <h3 style={{ color: theme.text, marginBottom: 2 }}>
                {owner.bname || 'Business Name'}
              </h3>
              <div style={{ color: theme.subtext, fontSize: '0.9rem' }}>
                {owner.fname} {owner.lname} &mdash; {owner.email}
              </div>
            </Col>
            <Col xs='auto'>
              <Badge
                bg={billingBadgeVariant(owner.billingStatus)}
                style={{ fontSize: '0.85rem' }}
              >
                {owner.billingStatus || 'none'}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        {/* ── Section 2: Business Info + Password ─────────────── */}
        <Col md={5}>
          <Card className='mb-4' style={cardStyle}>
            <Card.Body>
              <h3 style={{ color: theme.text }}>Business Info</h3>
              <p
                style={{
                  color: theme.subtext,
                  fontSize: '0.85rem',
                  marginBottom: '1rem'
                }}
              >
                To update your contact information, please contact our{' '}
                <a href='/contact' style={{ color: theme.accent }}>
                  help center
                </a>
                .
              </p>
              <Form>
                <Form.Group controlId='phone' className='mb-2'>
                  <Form.Label>Phone</Form.Label>
                  <Form.Control
                    readOnly
                    type='text'
                    value={owner.phone || ''}
                    style={{
                      backgroundColor: theme.surfaceMuted,
                      color: theme.subtext,
                      borderColor: theme.sidebarAccent
                    }}
                  />
                </Form.Group>
                <Form.Group controlId='address' className='mb-2'>
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    readOnly
                    type='text'
                    value={owner.address || ''}
                    style={{
                      backgroundColor: theme.surfaceMuted,
                      color: theme.subtext,
                      borderColor: theme.sidebarAccent
                    }}
                  />
                </Form.Group>
                <Row>
                  <Col>
                    <Form.Group controlId='city' className='mb-2'>
                      <Form.Label>City</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.city || ''}
                        style={{
                          backgroundColor: theme.surfaceMuted,
                          color: theme.subtext,
                          borderColor: theme.sidebarAccent
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId='state' className='mb-2'>
                      <Form.Label>State</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.state || ''}
                        style={{
                          backgroundColor: theme.surfaceMuted,
                          color: theme.subtext,
                          borderColor: theme.sidebarAccent
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Group controlId='country' className='mb-2'>
                      <Form.Label>Country</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.country || ''}
                        style={{
                          backgroundColor: theme.surfaceMuted,
                          color: theme.subtext,
                          borderColor: theme.sidebarAccent
                        }}
                      />
                    </Form.Group>
                  </Col>
                  <Col>
                    <Form.Group controlId='mailcode' className='mb-2'>
                      <Form.Label>Mail Code</Form.Label>
                      <Form.Control
                        readOnly
                        type='text'
                        value={owner.mailcode || ''}
                        style={{
                          backgroundColor: theme.surfaceMuted,
                          color: theme.subtext,
                          borderColor: theme.sidebarAccent
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          <Card className='mb-4' style={cardStyle}>
            <Card.Body>
              <h3 style={{ color: theme.text }}>Change Password</h3>
              {pwMessage && (
                <Alert
                  variant={pwMessage.type}
                  dismissible
                  onClose={() => setPwMessage(null)}
                >
                  {pwMessage.text}
                </Alert>
              )}
              <Form onSubmit={passwordSubmitHandler}>
                <Form.Group controlId='password' className='mb-2'>
                  <Form.Label>New Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Enter new password'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      backgroundColor: theme.bg,
                      color: theme.text,
                      borderColor: theme.sidebarAccent
                    }}
                  />
                </Form.Group>
                <Form.Group controlId='confirmPassword' className='mb-2'>
                  <Form.Label>Confirm Password</Form.Label>
                  <Form.Control
                    type='password'
                    placeholder='Confirm new password'
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    style={{
                      backgroundColor: theme.bg,
                      color: theme.text,
                      borderColor: theme.sidebarAccent
                    }}
                  />
                </Form.Group>
                <Button
                  type='submit'
                  variant='outline-primary'
                  className='mt-2'
                  style={accentBtn}
                >
                  Update Password
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* ── Section 3 + 4: Subscription + Private Keys ──────── */}
        <Col md={7}>
          {/* Section 3: Subscription Card */}
          <Card className='mb-4' style={cardStyle}>
            <Card.Body>
              <h3 style={{ color: theme.text }}>Subscription</h3>
              <Row className='mb-3'>
                <Col xs={5} style={{ color: theme.subtext }}>
                  Status
                </Col>
                <Col>
                  <Badge bg={billingBadgeVariant(owner.billingStatus)}>
                    {owner.billingStatus || 'none'}
                  </Badge>
                </Col>
              </Row>
              <Row className='mb-3'>
                <Col xs={5} style={{ color: theme.subtext }}>
                  Billing Period Start
                </Col>
                <Col style={{ color: theme.text }}>{billingPeriodDisplay}</Col>
              </Row>
              {owner.stripeSubscriptionId && (
                <Row className='mb-3'>
                  <Col xs={5} style={{ color: theme.subtext }}>
                    Subscription ID
                  </Col>
                  <Col
                    style={{
                      color: theme.text,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      wordBreak: 'break-all'
                    }}
                  >
                    {owner.stripeSubscriptionId}
                  </Col>
                </Row>
              )}
            </Card.Body>
            <Card.Footer style={footerStyle}>
              <Button
                size='sm'
                variant='outline-primary'
                style={accentBtn}
                onClick={() => navigate('/billing')}
              >
                Manage Billing
              </Button>
            </Card.Footer>
          </Card>

          {/* Section 4: Private Keys + Last Login */}
          <Card className='mb-4' style={cardStyle}>
            <Card.Body>
              <h3 style={{ color: theme.text }}>Private Keys</h3>
              <Form.Group controlId='apikey' className='mb-3'>
                <Form.Label style={{ color: theme.subtext }}>
                  API Key
                </Form.Label>
                <Row className='g-2'>
                  <Col>
                    <Form.Control
                      readOnly
                      type='text'
                      value={owner.apiKey || ''}
                      style={{
                        backgroundColor: theme.surfaceMuted,
                        color: theme.subtext,
                        borderColor: theme.sidebarAccent,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem'
                      }}
                    />
                  </Col>
                </Row>
              </Form.Group>
              <Form.Group controlId='cid' className='mb-3'>
                <Form.Label style={{ color: theme.subtext }}>
                  Customer ID
                </Form.Label>
                <Row className='g-2'>
                  <Col>
                    <Form.Control
                      readOnly
                      type='text'
                      value={owner.cid || ''}
                      style={{
                        backgroundColor: theme.bg,
                        color: theme.text,
                        borderColor: theme.sidebarAccent,
                        fontFamily: 'monospace',
                        fontSize: '0.8rem'
                      }}
                    />
                  </Col>
                  <Col xs='auto'>
                    <Button
                      variant='outline-secondary'
                      size='sm'
                      style={{ ...accentBtn, height: '100%' }}
                      onClick={() => copyToClipboard(owner.cid, setCidCopied)}
                    >
                      {cidCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </Col>
                </Row>
              </Form.Group>

              <hr style={{ borderColor: theme.sidebarAccent }} />
              <h3
                style={{ color: theme.text, fontSize: '1rem', marginBottom: 8 }}
              >
                Last Login
              </h3>
              <Row>
                <Col
                  xs={4}
                  style={{ color: theme.subtext, fontSize: '0.9rem' }}
                >
                  Date
                </Col>
                <Col style={{ color: theme.text, fontSize: '0.9rem' }}>
                  {lastLoginDate}
                </Col>
              </Row>
              <Row className='mt-1'>
                <Col
                  xs={4}
                  style={{ color: theme.subtext, fontSize: '0.9rem' }}
                >
                  Location
                </Col>
                <Col style={{ color: theme.text, fontSize: '0.9rem' }}>
                  {lastLoginLocation}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Section 5: Danger Zone ───────────────────────────── */}
      <Accordion className='mb-4'>
        <Accordion.Item
          eventKey='0'
          style={{ backgroundColor: theme.card, borderColor: '#dc3545' }}
        >
          <Accordion.Header>
            <span style={{ color: '#dc3545', fontWeight: 600 }}>
              ⚠ Danger Zone
            </span>
          </Accordion.Header>
          <Accordion.Body style={{ backgroundColor: theme.card }}>
            <Row>
              <Col md={6} className='mb-3'>
                <Card
                  style={{
                    backgroundColor: theme.surfaceMuted,
                    borderColor: '#dc3545',
                    borderRadius: '8px'
                  }}
                >
                  <Card.Body>
                    <Card.Title style={{ color: theme.text, fontSize: '1rem' }}>
                      Disable Account
                    </Card.Title>
                    <Card.Text
                      style={{ color: theme.subtext, fontSize: '0.85rem' }}
                    >
                      Temporarily disable your account. You will be logged out
                      and cannot log back in until re-enabled by an
                      administrator.
                    </Card.Text>
                    <Button
                      variant='outline-warning'
                      size='sm'
                      onClick={() => setShowDisableModal(true)}
                    >
                      Disable My Account
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} className='mb-3'>
                <Card
                  style={{
                    backgroundColor: theme.surfaceMuted,
                    borderColor: '#dc3545',
                    borderRadius: '8px'
                  }}
                >
                  <Card.Body>
                    <Card.Title style={{ color: theme.text, fontSize: '1rem' }}>
                      Delete Account
                    </Card.Title>
                    <Card.Text
                      style={{ color: theme.subtext, fontSize: '0.85rem' }}
                    >
                      Permanently delete your account and all associated data.
                      This action cannot be undone.
                    </Card.Text>
                    <Button
                      variant='outline-danger'
                      size='sm'
                      onClick={() => {
                        setConfirmText('')
                        setShowDeleteModal(true)
                      }}
                    >
                      Delete My Account
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      {/* Disable Confirmation Modal */}
      <Modal
        show={showDisableModal}
        onHide={() => setShowDisableModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Modal.Title style={{ color: theme.text }}>
            Disable Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme.card, color: theme.text }}>
          Are you sure you want to disable your account? You will be logged out
          immediately and will need an administrator to re-enable access.
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Button
            variant='secondary'
            onClick={() => setShowDisableModal(false)}
          >
            Cancel
          </Button>
          <Button variant='warning' onClick={handleDisableConfirm}>
            Yes, Disable My Account
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Modal.Title style={{ color: '#dc3545' }}>Delete Account</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: theme.card, color: theme.text }}>
          <p>
            This will permanently delete your account and all associated data.
            Type your <strong>business name</strong> to confirm:
          </p>
          <p>
            <code style={{ color: theme.accent }}>{owner.bname}</code>
          </p>
          <Form.Control
            type='text'
            placeholder='Type business name to confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            style={{
              backgroundColor: theme.bg,
              color: theme.text,
              borderColor: theme.sidebarAccent
            }}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: theme.card,
            borderColor: theme.sidebarAccent
          }}
        >
          <Button variant='secondary' onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            onClick={handleDeleteConfirm}
            disabled={confirmText !== owner.bname}
          >
            Delete My Account
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default OwnerProfileScreen
